import React, { useState } from 'react';
import './ContactUs.css';
import FAQ from '../../components/FAQ/FAQ';
import API_ENDPOINTS from '../../api';
import locationIcon from '../../assets/icons/location-icon.svg';
import phoneIcon from '../../assets/icons/phone-icon.svg';
import emailIcon from '../../assets/icons/email-icon.svg';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [responseType, setResponseType] = useState(null); // 'success' or 'error'

  const capitalizeFirstLetter = (str) => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone number, only allow numeric characters
    if (name === 'phoneNumber') {
      const numericValue = value.replace(/\D/g, ''); // Remove all non-digit characters
      setFormData({
        ...formData,
        [name]: numericValue
      });
    } else if (name === 'firstName' || name === 'lastName') {
      // Capitalize first letter for firstName and lastName
      const capitalizedValue = capitalizeFirstLetter(value);
      setFormData({
        ...formData,
        [name]: capitalizedValue
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage(null);
    setResponseType(null);

    try {
      const response = await fetch(API_ENDPOINTS.CONTACT.SUBMIT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Success response
        setResponseType('success');
        setResponseMessage('Thank you for contacting us! We\'ll get back to you soon.'|| data.status?.message || data.message || data.data?.message);
        setSubmitted(true);
        // Reset form
        setFormData({ firstName: '', lastName: '', email: '', phoneNumber: '', message: '' });
        
        // Clear message after 5 seconds
        setTimeout(() => {
          setResponseMessage(null);
          setResponseType(null);
          setSubmitted(false);
        }, 5000);
      } else {
        // Error response
        setResponseType('error');
        setResponseMessage( 'Something went wrong. Please try again later.'|| data.status?.message || data.message || data.error);
      }
    } catch (error) {
      // Network or other errors
      setResponseType('error');
      setResponseMessage('Network error. Please check your connection and try again.');
      console.error('Contact form submission error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-us-page">
      <div className="contact-container-wrapper">
        <div className="page-header-contact">
          <h1>Contact Us</h1>
          <p className="page-subtitle">Smartphone Diagnosis App</p>
        </div>

        <div className="contact-container">
          <div className="contact-info">
            <h2>Reach out at</h2>
            <p className="contact-intro">
              Questions, comments, or suggestions? Simply fill in the form and we'll be in touch shortly.
            </p>
            
            <div className="info-item">
              <div className="info-item-header">
                <img src={locationIcon} alt="Location" />
                <span><strong>Head Office:</strong> Platina Tower, Bristol Chowk, MG Road, Near Sikanderpur Metro Station, Sector 28, Gurugram, Haryana - 122001</span>
              </div>
            </div>

            <div className="info-item">
              <div className="info-item-header">
                <img src={locationIcon} alt="Location" />
                <span><strong>US Office (San Francisco Bay Area):</strong> 1020 Landmark Court, Richmond, CA, 94806, USA.</span>
              </div>
            </div>

            <div className="info-item-row">
              <div className="info-item">
                <div className="info-item-header" style={{ fontSize: '16px' ,fontWeight: '400' }}>
                  <img src={phoneIcon} alt="Phone" />
                  <span>+91 93150 93182</span>
                </div>
              </div>

              <div className="info-item">
                <div className="info-item-header">
                  <img src={emailIcon} alt="Email" />
                  <a href="mailto:info@phonevue.in" style={{ fontSize: '16px' ,fontWeight: '400' , color: '#212121' ,textDecoration: 'none' }}>info@phonevue.in</a>
                </div>
              </div>
            </div>

            <p className="contact-note">
              <span style={{ color: 'red' }}>*</span>Note: Email reply will be done at the earliest but might exceed more than 24 hours of receiving the email from the customer and only after verifying and checking with the technical team, a reply will be done from our side.
            </p>
          </div>

          <div className="contact-form-container">
            {responseMessage && (
              <div className={responseType === 'success' ? 'success-message' : 'error-message'}>
                <div>{responseMessage}</div>
              </div>
            )}
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    required
                  />
                </div>
                <div className="form-group">
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  required
                />
              </div>

              <div className="form-group">
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  onKeyPress={(e) => {
                    // Prevent non-numeric characters from being typed
                    if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Phone Number"
                  required
                />
              </div>

              <div className="form-group">
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Your Message..."
                  rows="6"
                  required
                ></textarea>
              </div>

              <button type="submit" className="submit-button" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>

      <section className="faq-section">
        <FAQ />
      </section>
    </div>
  );
};

export default ContactUs;
